import React from 'react';
import { Helmet } from 'react-helmet-async';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-grid" data-testid="terms-of-service-page">
      <Helmet>
        <title>Terms of Service | Wowrolla</title>
        <meta
          name="description"
          content="Comprehensive Terms of Service for Wowrolla, Ottawa, Ontario, Canada, including use terms, disclaimers, and legal provisions."
        />
      </Helmet>

      <section className="container mx-auto px-6 pb-14 max-w-5xl">
        <h1 className="text-4xl font-black mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
          Terms of Service
        </h1>
        <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
          Effective date: April 7, 2026 | Governing location: Ottawa, Ontario, Canada
        </p>

        <div className="terminal-grid p-6 space-y-5" style={{ color: 'var(--text-secondary)' }}>
          <p>
            These Terms of Service (Terms) govern your access to and use of Wowrolla websites, applications, content,
            and services. By using Wowrolla, you agree to these Terms.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>1. Eligibility and Acceptance</h2>
          <p>
            You represent that you are legally capable of entering into binding agreements. If using Wowrolla on behalf
            of an entity, you represent authority to bind that entity.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>2. Services</h2>
          <p>
            Wowrolla provides AI tool discovery content, comparison insights, recommendations, and related advisory features.
            Service features may change over time.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>3. Accounts and Security</h2>
          <p>
            You are responsible for maintaining confidentiality of account credentials and for all activity under your account.
            Notify us promptly of unauthorized access.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>4. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Violate any applicable law, regulation, or third-party rights.</li>
            <li>Reverse engineer, scrape at harmful scale, or interfere with service integrity.</li>
            <li>Upload malicious code, abuse APIs, or attempt unauthorized access.</li>
            <li>Use Wowrolla content to mislead users or conduct fraud.</li>
          </ul>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>5. Intellectual Property</h2>
          <p>
            Wowrolla and its licensors retain all rights in site content, branding, software, and documentation,
            except where otherwise stated. No rights are granted except as expressly set forth.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>6. Affiliate and Third-Party Relationships — Canadian Disclosure</h2>
          <p>
            Wowrolla may include affiliate links and receive commissions from qualifying actions.
            Third-party products and services are governed by their own terms and policies.
          </p>
          <p className="text-sm border border-yellow-500/30 bg-yellow-500/5 rounded p-3">
            <strong>Competition Bureau of Canada disclosure:</strong> We earn commissions on some tool links. This constitutes
            a material connection between Wowrolla and the tool providers, disclosed in accordance with the{' '}
            <em>Competition Act</em> (Canada). Disclosure appears in the footer, on every tool page, and on our{' '}
            <a href="/transparency" className="underline hover:text-cyber-yellow">Transparency page</a>.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>7. No Professional Advice</h2>
          <p>
            Content is informational and does not constitute legal, financial, accounting, tax, compliance, or security advice.
            You are responsible for independent evaluation before adopting tools or business changes.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>8. Disclaimer of Warranties</h2>
          <p>
            Wowrolla is provided as is and as available, without warranties of any kind, express or implied,
            including merchantability, fitness for a particular purpose, and non-infringement to the extent permitted by law.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Wowrolla and its affiliates will not be liable for indirect, incidental,
            special, consequential, or punitive damages, or for loss of profits, data, goodwill, or business opportunities.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>10. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Wowrolla from claims, liabilities, and expenses arising from your use
            of the service, violation of these Terms, or infringement of third-party rights.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>11. Suspension and Termination</h2>
          <p>
            We may suspend or terminate access at our discretion for abuse, legal risk, policy violations, or operational reasons.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>12. Governing Law and Venue</h2>
          <p>
            These Terms are governed by the laws of Ontario and applicable federal laws of Canada, including PIPEDA,
            the Competition Act, and CASL. Venue for disputes is Ottawa, Ontario, unless otherwise required by law.
            Quebec residents also have rights under Quebec's <em>Act Respecting the Protection of Personal Information
            in the Private Sector</em> (Law 25).
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>13. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time. Continued use after updates constitutes acceptance of revised Terms.
          </p>

          <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>14. Contact</h2>
          <p>
            Wowrolla Legal<br />
            Ottawa, Ontario, Canada<br />
            Email: support@wowrolla.ca
          </p>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;

